<template>
    <div class="team-dashboard">
        <skeleton-loader v-if="loadingRoster" type="dashboard" />

        <v-alert v-else-if="roster.length === 0" text color="secondary" border="left" class="mt-2">
            {{ $t('common.no_students') }}
        </v-alert>

        <div v-else class="dashboard-grid">
            <!-- FILA 1: Bienestar + Alarmas -->
            <div class="dash-row">
                <div class="dash-card section-wellness">
                    <div class="wellness-header">
                        <div class="wellness-header-left">
                            <div class="dash-title">{{ $t('dashboard.team_wellness') }}</div>
                            <div class="dash-meta">
                                {{ $t('dashboard.today') }} · {{ todayLabel }} ·
                                {{ $t('dashboard.responses_of', { r: responders, t: filteredRoster.length }) }}
                            </div>
                        </div>
                        <div v-if="!teamId" class="group-tabs">
                            <button v-for="chip in chips" :key="chip.id" class="group-tab"
                                :class="{ active: chip.id === activeChip }" @click="activeChip = chip.id">
                                {{ chip.name }}
                            </button>
                        </div>
                    </div>

                    <div class="wellness-circles">
                        <wellness-ring v-for="dim in ringData" :key="dim.key" :value="dim.value" :label="dim.label"
                            :icon="dim.icon" />
                    </div>

                    <div class="wellness-legend">
                        <div class="legend-item"><span class="legend-dot" style="background:#009966" />{{ $t('dashboard.optimal') }}</div>
                        <div class="legend-item"><span class="legend-dot" style="background:var(--v-primary-base)" />{{ $t('dashboard.moderate') }}</div>
                        <div class="legend-item"><span class="legend-dot" style="background:var(--v-error-base)" />{{ $t('dashboard.alert') }}</div>
                        <div v-if="ringsInAlert > 0" class="legend-alert">
                            <v-icon size="13" color="error">mdi-alert-outline</v-icon>
                            {{ $tc('dashboard.indicators_attention', ringsInAlert, { n: ringsInAlert }) }}
                        </div>
                    </div>
                </div>

                <div class="dash-card side-card">
                    <div class="side-header">
                        <span class="side-title">{{ $t('dashboard.alarms_today') }}</span>
                        <span class="count-badge alert" v-if="alarms.length">{{ alarms.length }}</span>
                    </div>
                    <div class="side-body">
                        <div v-for="a in alarms" :key="a.id" class="side-row">
                            <div v-if="a.avatar" class="side-avatar" :style="{ backgroundImage: `url(${a.avatar})` }" />
                            <div v-else class="side-avatar-initials" :style="a.avatarGradient">{{ a.initials }}</div>
                            <div class="side-info">
                                <div class="side-name">{{ a.name }}</div>
                                <div class="side-detail alert">{{ a.detail }}</div>
                            </div>
                        </div>
                        <div v-if="alarms.length === 0" class="side-empty">{{ $t('dashboard.no_alarms') }}</div>
                    </div>
                </div>
            </div>

            <!-- FILA 2: Carga (RPE/ACWR) + Lesionados -->
            <div class="dash-row">
                <div class="dash-card section-load">
                    <div class="wellness-header">
                        <div class="wellness-header-left">
                            <div class="dash-title">{{ $t('dashboard.rpe_per_session', { n: loadDates.length }) }}</div>
                            <div class="dash-meta">{{ acwrEnabled ? $t('dashboard.acwr_caption') : $t('dashboard.rpe_caption') }}</div>
                        </div>
                        <div class="load-legend">
                            <span v-if="acwrEnabled" class="ll-item"><span class="ll-line" />ACWR</span>
                            <span class="ll-item"><span class="ll-bar" />RPE</span>
                        </div>
                    </div>
                    <div class="load-chart-wrap">
                        <skeleton-loader v-if="loadingLoad" type="bar-chart" />
                        <team-load-chart v-else-if="loadDates.length" :labels="loadLabels" :rpe="loadRpe"
                            :acwr="loadAcwr" :styles="chartStyles" />
                        <div v-else class="side-empty center">{{ $t('common.without_data') }}</div>
                    </div>
                </div>

                <div class="dash-card side-card">
                    <div class="side-header">
                        <span class="side-title">{{ $t('dashboard.recent_injuries') }}</span>
                        <span class="count-badge lime" v-if="injured.length">
                            {{ $tc('dashboard.active_count', injured.length, { n: injured.length }) }}
                        </span>
                    </div>
                    <div class="side-body">
                        <skeleton-loader v-if="loadingInjuries" type="dash-list" :count="3" />
                        <template v-else>
                            <div v-for="inj in injured" :key="inj.id" class="side-row">
                                <div v-if="inj.avatar" class="side-avatar" :style="{ backgroundImage: `url(${inj.avatar})` }" />
                                <div v-else class="side-avatar-initials" :style="inj.avatarGradient">{{ inj.initials }}</div>
                                <div class="side-info">
                                    <div class="side-name">{{ inj.name }}</div>
                                    <div class="side-detail">{{ inj.detail }}</div>
                                </div>
                                <injury-badge v-if="inj.complexity" dense :complexity="inj.complexity" />
                            </div>
                            <div v-if="injured.length === 0" class="side-empty">{{ $t('dashboard.no_injuries') }}</div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { UserIndex } from '@/utils/Algolia'
import { db } from '@/utils/Firebase'
import { fetchStudentsAdvances } from '@/utils/Statistics'
import { getInitials, getAvatarGradient, civilDaysBetween } from '@/utils/Common'
import WellnessRing from '@/components/common/WellnessRing'
import TeamLoadChart from '@/components/coach/TeamLoadChart'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import InjuryBadge from '@/components/common/InjuryBadge'

const LOAD_WINDOW_DAYS = 56
const ACUTE_DAYS = 7
const CHRONIC_DAYS = 28
const ALERT_THRESHOLD = 5
const RECENT_INJURY_DAYS = 60

export default {
    name: 'HomeTeamDashboard',
    components: { WellnessRing, TeamLoadChart, SkeletonLoader, InjuryBadge },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        teamId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            roster: [],
            loadingRoster: false,
            rosterLoaded: false,
            activeChip: 'all',
            injuries: [],
            loadingInjuries: false,
            loadRoutines: [],
            loadingLoad: false,
        }
    },
    computed: {
        ...mapState(['teams']),
        dims(vm = this) {
            return [
                { key: 'energy', label: vm.$t('common.energy'), icon: 'mdi-lightning-bolt', invert: false },
                { key: 'sleep_night', label: vm.$t('common.sleep_night'), icon: 'mdi-weather-night', invert: false },
                { key: 'tired', label: vm.$t('dashboard.fatigue'), icon: 'mdi-battery-low', invert: true },
                { key: 'stressed', label: vm.$t('common.stressed'), icon: 'mdi-brain', invert: true },
                { key: 'muscles_hurt', label: vm.$t('dashboard.muscular'), icon: 'mdi-arm-flex', invert: true },
            ]
        },
        today(vm = this) {
            return vm.$moment().format('DD-MM-YYYY')
        },
        todayLabel(vm = this) {
            return vm.$moment().format('D MMM').replace('.', '')
        },
        chips(vm = this) {
            return [
                { id: 'all', name: vm.$t('common.all') },
                ...vm.teams.map(t => ({ id: t.id, name: t.name })),
            ]
        },
        filteredRoster(vm = this) {
            if (vm.teamId || vm.activeChip === 'all') return vm.roster
            return vm.roster.filter(s => s.teamId === vm.activeChip)
        },
        todayEntries(vm = this) {
            const entries = []
            vm.filteredRoster.forEach(s => {
                const entry = (s.lastWellness || []).find(w => w && w.date === vm.today)
                if (entry) entries.push({ student: s, entry })
            })
            return entries
        },
        responders(vm = this) {
            return vm.todayEntries.length
        },
        ringData(vm = this) {
            return vm.dims.map(dim => {
                const values = vm.todayEntries
                    .map(e => vm.goodValue(e.entry[dim.key], dim.invert))
                    .filter(v => v > 0)
                const value = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0
                return { key: dim.key, label: dim.label, icon: dim.icon, value: vm.round1(value) }
            })
        },
        ringsInAlert(vm = this) {
            return vm.ringData.filter(r => r.value > 0 && r.value < ALERT_THRESHOLD).length
        },
        alarms(vm = this) {
            const rows = []
            vm.todayEntries.forEach(({ student, entry }) => {
                const low = []
                vm.dims.forEach(dim => {
                    const good = vm.goodValue(entry[dim.key], dim.invert)
                    if (good > 0 && good < ALERT_THRESHOLD) low.push({ label: dim.label, value: vm.round1(good) })
                })
                if (low.length) {
                    low.sort((a, b) => a.value - b.value)
                    rows.push({
                        id: student.id,
                        name: student.name,
                        avatar: student.avatar,
                        initials: student.initials,
                        avatarGradient: student.avatarGradient,
                        detail: low.map(l => `${l.label} ${l.value}`).join(' · '),
                        severity: low[0].value,
                    })
                }
            })
            return rows.sort((a, b) => a.severity - b.severity)
        },
        injured(vm = this) {
            const ids = new Set(vm.filteredRoster.map(s => s.id))
            return vm.injuries
                .filter(inj => ids.has(inj.studentId) && vm.isActiveInjury(inj) && vm.isRecentInjury(inj))
                .map(inj => {
                    const student = vm.roster.find(s => s.id === inj.studentId) || {}
                    const startM = vm.parseDate(inj.date)
                    const days = startM ? civilDaysBetween(startM.format('YYYY-MM-DD'), vm.$moment().format('YYYY-MM-DD')) : null
                    const parts = []
                    if (inj.type) parts.push(vm.injuryTypeLabel(inj.type))
                    if (inj.zone) parts.push(vm.injuryZoneLabel(inj.zone))
                    if (days != null) parts.push(days <= 0 ? vm.$t('dashboard.today') : vm.$tc('dashboard.days_ago', days, { n: days }))
                    return {
                        id: inj.id,
                        name: student.name || '',
                        avatar: student.avatar,
                        initials: student.initials,
                        avatarGradient: student.avatarGradient,
                        detail: parts.join(' · '),
                        complexity: inj.complexity,
                    }
                })
        },
        loadByDate(vm = this) {
            const ids = new Set(vm.filteredRoster.map(s => s.id))
            const map = {}
            vm.loadRoutines.forEach(r => {
                if (!ids.has(r.studentId)) return
                const post = r.advance && r.advance.post ? r.advance.postInfo : null
                const date = r.rawDate && r.rawDate.full
                if (!post || !date) return
                const duration = Number(post.duration || 0)
                const effort = Number(post.effort || 0)
                if (!effort) return
                if (!map[date]) map[date] = { effortSum: 0, count: 0, charge: 0 }
                map[date].effortSum += effort
                map[date].count += 1
                map[date].charge += duration * effort
            })
            return map
        },
        loadDates(vm = this) {
            return Object.keys(vm.loadByDate).sort().slice(-6)
        },
        loadLabels(vm = this) {
            return vm.loadDates.map(d => vm.$moment(d, 'YYYY-MM-DD').format('D/M'))
        },
        loadRpe(vm = this) {
            return vm.loadDates.map(d => {
                const b = vm.loadByDate[d]
                return vm.round1(b.effortSum / b.count)
            })
        },
        acwrEnabled() {
            return LOAD_WINDOW_DAYS >= CHRONIC_DAYS
        },
        loadAcwr(vm = this) {
            if (!vm.acwrEnabled) return []
            return vm.loadDates.map(d => vm.acwrAt(d))
        },
        chartStyles() {
            return { position: 'relative', height: '100%', width: '100%' }
        },
    },
    mounted() {
        this.loadAll()
    },
    methods: {
        async loadAll() {
            const vm = this
            if (vm.rosterLoaded || (!vm.coachId && !vm.teamId)) return
            await vm.loadRoster()
            if (vm.roster.length === 0) return
            vm.loadInjuries()
            vm.loadTeamRoutines()
        },
        async loadRoster() {
            const vm = this
            vm.loadingRoster = true
            try {
                const filter = vm.teamId
                    ? `team.id: ${vm.teamId} AND studentSubscription.paused != 1`
                    : `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
                const { hits } = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
                vm.roster = hits.map(hit => ({
                    id: hit.objectID,
                    name: hit.name || hit.email || '',
                    avatar: hit.avatar || null,
                    initials: getInitials(hit.name),
                    avatarGradient: getAvatarGradient(hit.objectID || hit.name),
                    lastWellness: hit.lastWellness || [],
                    hasInjury: hit.hasInjury || false,
                    teamId: hit.team ? hit.team.id : null,
                }))
                vm.rosterLoaded = true
            } catch (err) {
                console.log('loadRoster error', err)
            } finally {
                vm.loadingRoster = false
            }
        },
        async loadInjuries() {
            const vm = this
            const ids = vm.roster.filter(s => s.hasInjury).map(s => s.id)
            if (ids.length === 0) return
            vm.loadingInjuries = true
            try {
                const results = await Promise.all(
                    vm.chunk(ids, 10).map(chunk =>
                        db.collection('injuries').where('studentId', 'in', chunk).get()
                    )
                )
                vm.injuries = results.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })))
            } catch (err) {
                console.log('loadInjuries error', err)
            } finally {
                vm.loadingInjuries = false
            }
        },
        async loadTeamRoutines() {
            const vm = this
            const ids = vm.roster.map(s => s.id)
            if (ids.length === 0) return
            vm.loadingLoad = true
            const end = vm.$moment().format('YYYY-MM-DD')
            const start = vm.$moment().subtract(LOAD_WINDOW_DAYS - 1, 'days').format('YYYY-MM-DD')
            try {
                const advances = await fetchStudentsAdvances(vm.teamId
                    ? { teamId: vm.teamId, start, end }
                    : { coachId: vm.coachId, studentIds: ids, start, end })
                vm.loadRoutines = advances.map(a => ({
                    id: a.id,
                    studentId: a.studentId,
                    rawDate: a.rawDate,
                    advance: a.advance,
                }))
            } catch (err) {
                console.log('loadTeamRoutines error', err)
            } finally {
                vm.loadingLoad = false
            }
        },
        acwrAt(dateStr) {
            const vm = this
            const acute = vm.meanDailyCharge(dateStr, ACUTE_DAYS)
            const chronic = vm.meanDailyCharge(dateStr, CHRONIC_DAYS)
            return chronic > 0 ? vm.round2(acute / chronic) : 0
        },
        meanDailyCharge(endStr, days) {
            const vm = this
            const end = vm.$moment(endStr, 'YYYY-MM-DD')
            let sum = 0
            for (let i = 0; i < days; i++) {
                const key = end.clone().subtract(i, 'days').format('YYYY-MM-DD')
                sum += vm.loadByDate[key] ? vm.loadByDate[key].charge : 0
            }
            return sum / days
        },
        goodValue(raw, invert) {
            const n = Number(raw || 0)
            if (!n) return 0
            return invert ? (11 - n) : n
        },
        isActiveInjury(inj) {
            const vm = this
            const discharge = vm.parseDate(inj.dischargeDate)
            if (!discharge) return true
            return discharge.isSameOrAfter(vm.$moment(), 'day')
        },
        isRecentInjury(inj) {
            const vm = this
            const start = vm.parseDate(inj.date)
            if (!start) return false
            return civilDaysBetween(start.format('YYYY-MM-DD'), vm.$moment().format('YYYY-MM-DD')) <= RECENT_INJURY_DAYS
        },
        parseDate(value) {
            const vm = this
            if (!value) return null
            if (typeof value === 'object' && typeof value.toDate === 'function') return vm.$moment(value.toDate())
            const m = vm.$moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', vm.$moment.ISO_8601], true)
            return m.isValid() ? m : null
        },
        chunk(arr, size) {
            const out = []
            for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
            return out
        },
        round1(v) {
            return Math.round(v * 10) / 10
        },
        round2(v) {
            return Math.round(v * 100) / 100
        },
    },
    watch: {
        coachId(val) {
            if (val && !this.rosterLoaded) this.loadAll()
        },
    },
}
</script>

<style scoped>
.dashboard-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dash-row {
    display: grid;
    grid-template-columns: 1fr 240px;
    gap: 8px;
}

.dash-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.section-wellness,
.section-load {
    padding: 14px 18px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.wellness-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.dash-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.dash-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 1px;
}

.group-tabs {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.group-tab {
    padding: 3px 9px;
    border-radius: 6px;
    font-size: 11px;
    cursor: pointer;
    border: none;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    transition: all 0.15s;
}

.group-tab.active {
    background: var(--v-primary-base);
    color: #0C0E12;
    font-weight: 600;
}

.theme--light .group-tab.active {
    background: var(--v-accent-base);
    color: #fff;
}

.group-tab:not(.active):hover {
    color: var(--v-txtPrimary-base);
}

.wellness-circles {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.wellness-legend {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
    border-top: 1px solid var(--v-outline-base);
    padding-top: 10px;
    flex-wrap: wrap;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: var(--v-txtSecondary-base);
}

.legend-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
}

.legend-alert {
    margin-left: auto;
    font-size: 11px;
    font-weight: 500;
    color: var(--v-error-base);
    display: flex;
    align-items: center;
    gap: 4px;
}

/* Side cards (alarmas / lesionados) */
.side-card {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.side-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.side-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.count-badge {
    font-size: 11px;
    font-weight: 700;
    border-radius: 10px;
    padding: 2px 8px;
}

.count-badge.alert {
    color: var(--v-error-base);
    background: rgba(232, 92, 92, 0.1);
    border: 1px solid rgba(232, 92, 92, 0.2);
}

.count-badge.lime {
    color: #0C0E12;
    background: var(--v-primary-base);
    border: 1px solid var(--v-primary-base);
}

.theme--light .count-badge.lime {
    color: #fff;
    background: var(--v-accent-base);
    border-color: var(--v-accent-base);
}

.side-body {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.side-body::-webkit-scrollbar {
    width: 5px;
}

.side-body::-webkit-scrollbar-track {
    background: transparent;
}

.side-body::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 3px;
}

.side-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.side-row:last-child {
    border-bottom: none;
}

.side-avatar,
.side-avatar-initials {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
}

.side-avatar {
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
}

.side-avatar-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
}

.side-info {
    flex: 1;
    min-width: 0;
}

.side-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.side-detail {
    font-size: 10px;
    color: var(--v-txtSecondary-base);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.side-detail.alert {
    color: var(--v-error-base);
}

.side-empty {
    padding: 16px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    font-style: italic;
}

.side-empty.center {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

/* Load chart */
.load-legend {
    display: flex;
    align-items: center;
    gap: 14px;
}

.ll-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.ll-line {
    width: 20px;
    height: 2px;
    background: var(--v-secondary-base);
    border-radius: 2px;
}

.ll-bar {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: rgba(102, 204, 204, 0.35);
}

.load-chart-wrap {
    height: 240px;
    min-height: 0;
    position: relative;
}

@media (max-width: 960px) {
    .dash-row {
        grid-template-columns: 1fr;
    }

    .side-body {
        max-height: 300px;
    }
}
</style>
